import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import {  Units, colors, fonts, frames, glyphs, mediaQueriesNumbers, useMediaQuery } from '../../../Styles';
import { ListData_ValueTypes, TableStyles_Types, ButtonTypes } from '../../../Definitions'
import { ButtonList, Button } from '../../../Components';
import { FeedComponentInfo } from '../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/getFeedComponentsCall';
import { ReactiveTable } from '../../../Components/Tables/reactiveTableRenderer';
import { FeedCurveInfo } from '../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/getFeedCurvesCall';
import Graph_QL from '../../../Services/Agrisys_GqlDataCaller';
import { convertDenseToSparse, convertSparseToDense, FeedCurveTableData } from './convertSparseMixtureData';
import { useEditableColumnConstructor } from '../../../Components/Tables/_Tables/useEditableColumnConstructor';
import { useFeedCurves } from '../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/useFeedCurves';
import { merge } from 'lodash';

interface FeedCurveTableProps {
  showFeedCurve: FeedCurveInfo,
  feedComponents: FeedComponentInfo[],
  feedCurveComponentIds: number[],
  setDisableCurveNavigation: Dispatch<SetStateAction<boolean>>;
  connection: Graph_QL.Connection;
  watchFeedCurve?: () => void
}

const milestoneOptions = [{label: "Farrowing"},{label: "Insemination"},{label: "Weaning"},{label: "Moved to farrowing"}]

export const FeedCurveTable = ({showFeedCurve, feedComponents, feedCurveComponentIds, setDisableCurveNavigation, connection,watchFeedCurve}: FeedCurveTableProps) => {
  const [editTable, setEditTable] = useState(false);
  const [isPercentileEdit, setIsPercentileEdit] = useState(false);
  const [mixtureData, setMixtureData] = useState<FeedCurveTableData[]>([]);
  const {t} = useTranslation();
  const {maintainEntries, maintainMixture} = useFeedCurves(connection);

  const sparseData = useMemo(() => (
    convertDenseToSparse(showFeedCurve.mixture, showFeedCurve.milestones, feedCurveComponentIds, feedComponents, isPercentileEdit)
  ),[feedComponents, feedCurveComponentIds, isPercentileEdit, showFeedCurve.milestones, showFeedCurve.mixture])

  const feedCurveId = useMemo(() => (showFeedCurve.id),[showFeedCurve.id])
  const isScreenHeightMax = useMediaQuery(`(max-height: ${mediaQueriesNumbers.ScreenHeigtMax}px)`);
  const mobileXs = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileXs}px)`);

  useEffect(() => {
    setIsPercentileEdit(false);
  },[feedCurveId])

  const dirtRef = useRef(sparseData);
  const mixtureDataDirtRef = useRef<Map<number,Partial<FeedCurveTableData>>>(new Map());
  const milestoneDayChanged = useRef(false);
  
  useEffect(() => {
    const sparseData = convertDenseToSparse(showFeedCurve.mixture, showFeedCurve.milestones, feedCurveComponentIds, feedComponents, isPercentileEdit)
    setMixtureData(Array.from(sparseData.values()));
    if (!isPercentileEdit) {
      dirtRef.current = sparseData;
    }
  },[isPercentileEdit, sparseData])


  const accessorData = useMemo(() => ([
    {headerValue: t("feed.feedPlan.day"), accessor: "id", type: ListData_ValueTypes.Number,},
    {headerValue: t("feed.feedPlan.ds"), accessor: "ds", type: ListData_ValueTypes.Number, staticColumn: true, unit: Units.pct_white},
    {headerValue: t("units.total"), accessor: "totalAmount", type: ListData_ValueTypes.Number, staticColumn: !isPercentileEdit, unit: Units.kG_white},
    {headerValue: t("feed.feedPlan.milestone"), accessor: "milestone", type: ListData_ValueTypes.List, options: milestoneOptions,},
    {accessor: "removeRowActionColumn", headerValue: "notApplicable", width: "56px", type: ListData_ValueTypes.DeleteRowAction},
    ...feedCurveComponentIds.map((id: number) => ({
      accessor: String(id),
      staticColumn: isPercentileEdit,
      unit: isPercentileEdit ? Units.pct_white : Units.kG_white,
      idForColor: id,
      type: ListData_ValueTypes.Number,
      width: "minmax(190px, 1fr)",
      headerValue: feedComponents.find((component) => component.id === id)?.name ?? String(id),
    }))
  ])
  ,[feedCurveComponentIds, feedComponents, isPercentileEdit, t])
  const columns = useEditableColumnConstructor<FeedCurveTableData>({accessorData: [...accessorData], tableStyle: TableStyles_Types.feedCurveTable, canEdit: editTable })

  const table = useReactTable<FeedCurveTableData>({
    columns,
    data: mixtureData,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      columnOrder: ["milestone","id", ...feedCurveComponentIds.map((id)=>(String(id))), "removeRowActionColumn", "totalAmount", "ds"],
      columnPinning: {
        left: mobileXs? undefined : ["milestone", "id"],
        right: mobileXs? undefined : ["totalAmount", "ds"],
      },
    },
    meta: {
      updateData: (rowIndex, columnId, value) => {
        const row = dirtRef.current.get(rowIndex);
        if (row !== undefined) {
          if (columnId === "totalAmount" && value) { 
            const deviation = +value/row[columnId]
            const changes = feedCurveComponentIds.reduce((accumulator, currentValue) => {
              const oldAmount = row[currentValue];
              if (oldAmount) {
                const newAmount = +oldAmount*deviation
                row![currentValue] = newAmount
                return Object.assign(accumulator, {[currentValue]: +oldAmount*deviation})
              }
              return accumulator
            },{})
            mixtureDataDirtRef.current.set(rowIndex, changes)
            return
          }
          if (columnId === "milestone" || columnId === "id") {
              milestoneDayChanged.current = true;
          }
          row![columnId] = value;
          const mergedChanges = mixtureDataDirtRef.current.get(rowIndex);
          mixtureDataDirtRef.current.set(rowIndex, (merge({},{[columnId]:value}, mergedChanges)))
        }
      },
      deleteRow: (rowId) => {
        dirtRef.current.delete(rowId);
        setMixtureData(Array.from(dirtRef.current.values()));
      },
      allowDeleteRowCheck: (row) => {return (!row.original.milestone)}
    }
  })

  const clearDirt = () => {
    setMixtureData(Array.from(sparseData.values()));
    dirtRef.current = sparseData;

    mixtureDataDirtRef.current=new Map();
    milestoneDayChanged.current=false;
  }
  
  const switchTable = () => {
    setDisableCurveNavigation(!editTable);
    setEditTable((editTable) => !editTable);
  };

  const saveChanges = () => {
    setDisableCurveNavigation(!editTable);
    if (maintainEntries) {
      console.log(dirtRef.current);
      const {mixture, milestones} = convertSparseToDense(Array.from(dirtRef.current.values()), feedCurveComponentIds, feedComponents) 
      // TODO What if its milestones and or day we changes (We would most likely end up needing some more id here if thats the case if we don't work back from the FC)
      // we might have to do a full replace of the feedcurve if this is the case.
      if (milestoneDayChanged.current === true) {
        maintainEntries({con: connection.connection, data: [{...showFeedCurve, mixture, milestones }]})
      } else {
        maintainMixture({con: connection.connection, data: [{...showFeedCurve, mixture, milestones }]}, mixtureDataDirtRef.current)
      }
    }
  }

  const togglePercentileEdit = () => {
    setIsPercentileEdit((old) => !old)
  }

  const percentileEditHeader = isPercentileEdit ? t("units.kg", {defaultValue: "kg"}) : t("units.pct", {defaultValue: "pct"})

  const percentileEditIcon = isPercentileEdit ? glyphs.Kg({}) : glyphs.Percent({})

  return (
    <frames.feedCurveTableContainer>
      {/* FeedCurveTable Btns (toggle, edit...) */}
      <div style={{ backgroundColor: "transparent", display: "flex", justifyContent: "end", padding: "0px 20px", gap: "5px" }}>
        {(isScreenHeightMax || mobileXs) && 
        <Button 
          content={t("feed.feedCurve.watchFeedcurve")} 
          buttonType={ButtonTypes.textButton}
          overrides={{
            baseButton: {
              ...fonts.fontSize4,
              padding: "0 10px",
              height: "45px", 
              backgroundColor: colors.greySecondary,
            },
            hoverStyles: {
              backgroundColor: colors.greyPrimary,
              opacity: "1"
            }
          }}
          onClick={() => { if (watchFeedCurve) watchFeedCurve() }}
        />}
        {!editTable ?
          <ButtonList 
            contents={[percentileEditHeader, t("buttons.edit")]} 
            onClick={(i => {
              if (i === 0) togglePercentileEdit()
              else if (i === 1) switchTable()
            })}
            buttonType={ButtonTypes.tableButton}
            startEnhancers={[percentileEditIcon, glyphs.Edit({})]}
          />
          : <ButtonList 
              contents={[ t("buttons.saveBtn"), t("buttons.cancelBtn"), percentileEditHeader, t("buttons.edit")]} 
              onClick={(i => {
                if (i === 0) {saveChanges(); switchTable()}
                else if (i === 1) switchTable()
                else if (i === 2) {
                togglePercentileEdit()
                  clearDirt();
              }
            })}
              buttonType={ButtonTypes.tableButton}
              startEnhancers={[glyphs.Add({}), glyphs.Close({}), percentileEditIcon, glyphs.Edit({})]}
              disabled={[false, false, false, true]}
              overrides={(index) => ({
                baseButton: {
                  backgroundColor: index === 3 ? colors.greyPrimary : colors.greySecondary
                },
                disabledStyles: {
                  opacity: index === 3 ? 1 : 0.5
                }
              })}
            />
        }
      </div>
      {/* FeedCurveTable */}
      <ReactiveTable table={table} tableType={TableStyles_Types.feedCurveTable} emptyText={t("feed.feedCurve.emptyFeedcurve")} />
    </frames.feedCurveTableContainer>
  )
}